import { useState, useCallback } from 'react';
import { InventoryRecordsService } from '../api';
import { InventoryRecord, SupplierInfo, WarehouseProduct } from '../types/inventory.types';
import type {
  InventoryRecordDetailedItemsResponseDto,
  InventoryRecordDetailedResponseDto,
} from '../api';

export type InventoryRecordItemsPagination = {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
};

export type InventoryRecordPricingTotals = {
  totalCostPrice: number;
  totalSalePrice: number;
};

export type InventoryRecordDetailPage = InventoryRecord & {
  itemsPagination: InventoryRecordItemsPagination;
  pricingTotals: InventoryRecordPricingTotals;
};

type DetailedLike = {
  id: number;
  recordCode: unknown;
  status: string;
  createdAt: string;
  completedAt?: unknown;
  destinationLocation?: { id?: number; name?: string } | null;
  registeredBy?: {
    id: string;
    username?: string;
    fullName?: string;
  } | null;
  supplier?: { id?: number; name?: string } | null;
  totalItemsCount?: number;
  distinctProductsCount?: number;
  isDeletable?: boolean;
  items?: InventoryRecordDetailedResponseDto['items'];
  warehouseProducts?: InventoryRecordDetailedResponseDto['warehouseProducts'];
};

function mapRegisteredBy(
  registeredBy: DetailedLike['registeredBy'],
): InventoryRecord['registeredBy'] {
  if (!registeredBy?.id) return null;
  return {
    id: registeredBy.id,
    username: registeredBy.username,
    fullName: registeredBy.fullName,
  };
}

function asOptionalString(value: unknown): string | null {
  if (typeof value === 'string') return value;
  if (value == null) return null;
  return String(value);
}

function deriveSupplierFromDetailed(response: DetailedLike): SupplierInfo | undefined {
  if (response.supplier?.id != null) {
    return {
      id: response.supplier.id,
      name: response.supplier.name ?? '',
    };
  }
  const items = response.items ?? [];
  for (const item of items) {
    const units = item.individualUnits ?? [];
    for (const unit of units) {
      const sid = unit.supplierId;
      if (sid != null && sid > 0) {
        return { id: sid, name: '' };
      }
    }
  }
  return undefined;
}

function mapWarehouseProducts(
  warehouseProducts: DetailedLike['warehouseProducts'],
): WarehouseProduct[] {
  return (warehouseProducts ?? []).map((wp) => ({
    id: wp.id,
    productCodeUnique: wp.productCodeUnique,
    productName: wp.productName,
    productCode: wp.productCode,
    productType: wp.productType,
    productStatus: wp.productStatus,
    availabilityStatus: wp.availabilityStatus,
    status: wp.status,
    material: wp.material,
    physicalAttributes: wp.physicalAttributes,
    pricing: wp.pricing,
  }));
}

function mapDetailedCore(response: DetailedLike): InventoryRecord {
  const recordCodeStr = asOptionalString(response.recordCode) ?? '';
  const rawSt = String(response.status || '').toUpperCase();
  const completedAt = asOptionalString(response.completedAt);
  const registrationIso = rawSt === 'COMPLETED' ? completedAt : null;
  const derivedSupplier = deriveSupplierFromDetailed(response);

  return {
    id: response.id,
    recordCode: recordCodeStr,
    destinationLocationId: response.destinationLocation?.id ?? 0,
    location: response.destinationLocation?.name ?? 'Sin ubicación',
    date: registrationIso
      ? new Date(registrationIso).toLocaleString('es-ES', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        })
      : '—',
    createdAt: response.createdAt,
    completedAt,
    status: response.status,
    items: response.items ?? [],
    totalItemsCount: response.totalItemsCount ?? 0,
    distinctProductsCount: response.distinctProductsCount ?? 0,
    notes: undefined,
    warehouseProducts: mapWarehouseProducts(response.warehouseProducts),
    registeredBy: mapRegisteredBy(response.registeredBy),
    supplier: derivedSupplier,
    branch_id: response.destinationLocation?.id,
    destinationLocation:
      response.destinationLocation?.id != null
        ? {
            id: response.destinationLocation.id,
            name: response.destinationLocation.name ?? '',
          }
        : undefined,
    isDeletable: response.isDeletable,
  };
}

function mapPaginatedDetailed(
  response: InventoryRecordDetailedItemsResponseDto,
): InventoryRecordDetailPage {
  const core = mapDetailedCore(response);
  return {
    ...core,
    pricingTotals: {
      totalCostPrice: Number(response.pricingTotals?.totalCostPrice ?? 0),
      totalSalePrice: Number(response.pricingTotals?.totalSalePrice ?? 0),
    },
    itemsPagination: {
      page: response.pagination.page,
      limit: response.pagination.limit,
      total: response.pagination.total,
      totalPages: response.pagination.totalPages,
    },
  };
}

interface UseInventoryRecordReturn {
  record: InventoryRecord | null;
  loading: boolean;
  error: Error | null;
  /** Full payload (edit / print). Prefer fetchRecordPage for the view modal. */
  fetchRecord: (id: number) => Promise<InventoryRecord | null>;
  /** Header + one page of product lines (fast view modal). */
  fetchRecordPage: (
    id: number,
    page?: number,
    limit?: number,
  ) => Promise<InventoryRecordDetailPage | null>;
  resetState: () => void;
}

/**
 * Fetch inventory records for edit/print (full) or view modal (paginated).
 */
export function useInventoryRecord(): UseInventoryRecordReturn {
  const [record, setRecord] = useState<InventoryRecord | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);

  const resetState = useCallback(() => {
    setRecord(null);
    setError(null);
    setLoading(false);
  }, []);

  const fetchRecord = useCallback(async (id: number): Promise<InventoryRecord | null> => {
    setLoading(true);
    setError(null);

    try {
      const response = await InventoryRecordsService.inventoryRecordsControllerFindOneDetailed(
        id.toString(),
        true,
      );

      if (response == null) {
        throw new Error('Failed to fetch inventory record');
      }

      const inventoryRecord = mapDetailedCore(response);
      setRecord(inventoryRecord);
      return inventoryRecord;
    } catch (err) {
      console.error('Error fetching inventory record:', err);
      const nextError = err instanceof Error ? err : new Error('Failed to fetch inventory record');
      setError(nextError);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchRecordPage = useCallback(
    async (
      id: number,
      page: number = 1,
      limit: number = 100,
    ): Promise<InventoryRecordDetailPage | null> => {
      setLoading(true);
      setError(null);

      try {
        const response =
          await InventoryRecordsService.inventoryRecordsControllerFindOneDetailedItems(
            id,
            page,
            limit,
          );

        if (response == null) {
          throw new Error('Failed to fetch inventory record page');
        }

        const inventoryRecord = mapPaginatedDetailed(response);
        setRecord(inventoryRecord);
        return inventoryRecord;
      } catch (err) {
        console.error('Error fetching inventory record page:', err);
        const nextError =
          err instanceof Error ? err : new Error('Failed to fetch inventory record page');
        setError(nextError);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { record, loading, error, fetchRecord, fetchRecordPage, resetState };
}
